import {
  StudentDataItem,
  LoanTypeTotal,
  NsldsLoan, // Import NsldsLoan
  Loan,
  LoanEvent,
  ParsedData,
} from '@/types/nsldsTypes'; // Adjust path as needed
import { parse, isValid } from 'date-fns'; // Import date-fns functions
import { processLoanSection } from './loan-parser';

/**
 * Parses the main NSLDS text file content using section processors.
 *
 * @param lines - The lines of the text file.
 * @param existingLoans - Not used in this refactored version but kept for potential future use.
 * @param borrower - The owner of the loans being parsed ('user' | 'partner').
 * @returns Parsed data structure.
 */
function parseData(
  lines: string[],
  _existingLoans: Loan[],
  borrower: 'user' | 'partner'
): ParsedData {
  const studentData: StudentDataItem[] = [];
  const loanTypeTotalsData: LoanTypeTotal[] = []; // If needed, logic can be added
  const loansData: Loan[] = [];
  const allLoanEvents: LoanEvent[] = []; // Collect all events if needed
  let fileRequestDate: string | null = null;

  // Attempt to parse file request date for event calculation end boundary
  const fileRequestDateLine = lines.find((line) => line.trim().startsWith('File Request Date:'));
  if (fileRequestDateLine) {
    fileRequestDate = fileRequestDateLine.substring(fileRequestDateLine.indexOf(':') + 1).trim();
  }
  let fileEndDate = fileRequestDate ? parse(fileRequestDate, 'MM/dd/yyyy', new Date()) : null;
  if (!fileEndDate || !isValid(fileEndDate)) {
    fileEndDate = new Date(); // Default to now if file request date is invalid/missing
  }

  // 1. Split lines into sections (Initial Info, Loan 1, Loan 2, ...)
  const loanSections: string[][] = [];
  let currentSectionLines: string[] = [];
  const initialInfoLines: string[] = [];
  let foundFirstLoan = false;

  for (const line of lines) {
    const trimmedLine = line.trim();
    // Use 'Loan Type Code:' as the primary marker for a new loan section start
    // Assuming 'Loan Award ID:' might sometimes be missing or delayed in the section
    if (trimmedLine.startsWith('Loan Type Code:')) {
      foundFirstLoan = true;
      if (currentSectionLines.length > 0) {
        loanSections.push([...currentSectionLines]);
      }
      currentSectionLines = [trimmedLine]; // Start new section with this line
    } else if (foundFirstLoan) {
      currentSectionLines.push(trimmedLine);
    } else {
      // Collect lines before the first loan marker
      initialInfoLines.push(trimmedLine);
    }
  }
  // Add the last collected loan section
  if (currentSectionLines.length > 0) {
    loanSections.push([...currentSectionLines]);
  }

  // 2. Process Initial Info section (Student Data, Loan Type Totals)
  let currentLoanTypeTotal: LoanTypeTotal | null = null;
  for (const line of initialInfoLines) {
    const separatorIndex = line.indexOf(':');
    if (separatorIndex === -1 || !line) continue;
    const key = line.substring(0, separatorIndex).trim();
    const value = line.substring(separatorIndex + 1).trim();

    // Basic handling for student data and totals - can be expanded if needed
    if (key === 'Student First Name') {
      // Assuming Student Data starts here
      if (currentLoanTypeTotal) loanTypeTotalsData.push(currentLoanTypeTotal);
      currentLoanTypeTotal = null;
      studentData.push({ key, value });
    } else if (key.startsWith('Student ')) {
      studentData.push({ key, value });
    } else if (key === 'Type Code') {
      // Assuming Loan Type Total starts here
      if (currentLoanTypeTotal) loanTypeTotalsData.push(currentLoanTypeTotal);
      currentLoanTypeTotal = { [key]: value };
    } else if (currentLoanTypeTotal) {
      currentLoanTypeTotal[key] = value;
    }
    // Ignore other lines like File Request Date (already handled), etc.
  }
  // Push the last loan type total if it exists
  if (currentLoanTypeTotal) loanTypeTotalsData.push(currentLoanTypeTotal);

  // 3. Process each loan section using the helper
  for (const loanLines of loanSections) {
    try {
      const parsedLoan = processLoanSection(loanLines, borrower, fileEndDate);
      // Duplicate Check (Simplified - checking against already parsed loans in *this* file)
      if (loansData.some((existing) => existing['Loan Award ID'] === parsedLoan['Loan Award ID'])) {
        console.warn(
          `Duplicate loan detected within the same file: ID ${parsedLoan['Loan Award ID']}. Skipping.`
        );
        continue; // Skip adding this duplicate loan
      }
      loansData.push(parsedLoan);
      // Note: The event parser currently returns events, but we aren't collecting them globally here.
      // If needed, `processLoanSection` could return events, and we could collect them in `allLoanEvents`.
    } catch (error) {
      console.error(
        'Error processing loan section:',
        error,
        '; Section Lines:',
        loanLines.slice(0, 5)
      );
      // Decide if one error should halt all processing or just skip the section
      // throw new Error(`Failed to process loan section: ${error instanceof Error ? error.message : String(error)}`); // Option: Halt on error
    }
  }

  // Placeholder for allLoanKeys - Can be derived if needed by collecting keys used in loan-parser
  const allLoanKeys = new Set<keyof NsldsLoan>();

  return {
    studentData,
    loansData,
    loanEventsData: allLoanEvents, // Currently empty, needs adjustment if events are required globally
    allLoanKeys, // Placeholder
    fileRequestDate,
  };
}

/**
 * Processes the uploaded text file content.
 * Orchestrates splitting the file and calling section processors.
 *
 * @param fileContent The content of the uploaded text file.
 * @param existingLoans Existing loans to check against for duplicates.
 * @param borrower The owner of the loans being parsed.
 * @returns A promise that resolves with the parsed data.
 */
export async function processUploadedFile(
  fileContent: string,
  existingLoans: Loan[],
  borrower: 'user' | 'partner'
): Promise<ParsedData> {
  return new Promise((resolve, reject) => {
    try {
      const lines = fileContent.split('\n');
      // Pass borrower to the refactored parseData
      const parsedResult = parseData(lines, existingLoans, borrower);

      // Basic validation after parsing
      if (parsedResult.studentData.length === 0 && parsedResult.loansData.length === 0) {
        console.warn(
          'Parsing resulted in empty student and loan data. Check file format or splitting logic.'
        );
        // Consider if this should be a rejection or just a warning
      }

      // --- Duplicate Check Against *Existing* Loans (passed from App state) --- //
      const existingLoanIds = new Set(existingLoans.map((loan) => loan['Loan Award ID']));
      const finalLoansData = parsedResult.loansData.filter((newLoan) => {
        if (existingLoanIds.has(newLoan['Loan Award ID'])) {
          console.warn(
            `Duplicate loan detected (compared to existing loans): ID ${newLoan['Loan Award ID']}. Skipping.`
          );
          // Optionally, throw an error here if duplicates across files are strictly forbidden
          // reject(new Error(`Duplicate loan detected (compared to existing loans): ID ${newLoan['Loan Award ID']}`));
          return false; // Exclude duplicate
        }
        return true; // Include non-duplicate
      });

      if (finalLoansData.length < parsedResult.loansData.length) {
        // If duplicates were found and filtered, potentially throw or reject
        // For now, just log and continue with filtered data
        console.warn('Duplicate loans found when comparing against existing data were removed.');
        // Or: reject(new Error('Duplicate loans found when comparing against existing data.'));
      }

      resolve({ ...parsedResult, loansData: finalLoansData });
    } catch (error) {
      console.error('Error during file processing orchestration:', error);
      reject(error instanceof Error ? error : new Error(String(error)));
    }
  });
}
